<template>
	<div>
		<v-card class="pa-3">
			<AddCommentDialog :show-delete-button="false" :title="commentModalTitle" v-model="showCommentModal" @submit="handleCommentSubmission" />

			<v-card-title>{{ $t('report.overview') }}</v-card-title>
			<div class="report-form-body">
				<v-form ref="reportFormRef" v-model="isValid">
					<v-list-subheader class="ml-3">{{ $t('report.form.section.extra_work') }}</v-list-subheader>
					<v-container class="mb-3 ml-2 text-body-2">
						<v-row>
							<v-col cols="3" align-self="end" class="mb-5">
								{{ $t('date') }}
							</v-col>
							<v-col>
								<span>{{ formatDateTime(extraWork.createdAt) }}</span>
							</v-col>
						</v-row>
						<v-row>
							<v-col cols="3" align-self="end" class="mb-5">
								{{ $t('duty_duty') }}
							</v-col>
							<v-col>
								<span>{{ extraWork.dutyName }}</span>
							</v-col>
						</v-row>
						<v-row>
							<v-col cols="3">{{ $t('user_edit_status') }}</v-col>
							<v-col>
								<v-select
									:label="$t('please_choose')"
									:items="statues"
									v-model="extraWork.status"
									color="primary"
									item-color="primary"
									required
								></v-select>
							</v-col>
						</v-row>
						<v-row align="center" no-gutters>
							<v-col cols="3" class="body-2">
								{{ $t('absence.start') }}
							</v-col>

							<v-col class="pr-2">
								<DateEntryForm
									ref="startDateInput"
									v-model="extraWork.startDate"
									:hideIcon="true"
									:isMobile="true"
									hide-details="auto"
									:min="dutyDateISO"
									:max="nextDayISO"
									:rules="startDateRules"
								/>
							</v-col>

							<v-col cols="4">
								<TimeEntryForm
									v-model="extraWork.startTime"
									label="time"
									:hideIcon="true"
									:rules="startTimeRules"
									hide-details="auto"
									ref="startTimeInput"
								/>
							</v-col>
						</v-row>
						<v-row align="center" no-gutters class="mb-2">
							<v-col cols="3" align-self="center" class="text-body-2">
								{{ $t('absence.end') }}
							</v-col>

							<v-col class="pr-2">
								<DateEntryForm
									ref="endDateInput"
									v-model="extraWork.endDate"
									:hideIcon="true"
									:isMobile="true"
									:min="extraWork.startDate"
									:max="nextDayISO"
									:rules="endDateRules"
									hide-details="auto"
								></DateEntryForm>
							</v-col>

							<v-col cols="4">
								<TimeEntryForm
									v-model="extraWork.endTime"
									:label="$t('time')"
									:hideIcon="true"
									:rules="endTimeRules"
									hide-details="auto"
									ref="endTimeInput"
								></TimeEntryForm>
							</v-col>
						</v-row>
					</v-container>
					<v-list-subheader>
						<div class="ml-3">{{ $t('absence.reason') }}</div>
					</v-list-subheader>
					<v-container class="px-5">
						<v-row>
							<v-col>
								<v-select
									:label="$t('please_choose')"
									:items="reasons"
									v-model="extraWork.reason"
									color="primary"
									item-color="primary"
									:rules="ruleNotEmpty"
									required
								></v-select>
							</v-col>
						</v-row>
						<v-row v-if="extraWork.reason === $t('report.reason.other')">
							<v-col>
								<v-text-field v-model="extraWork.other" :label="$t('absence.reason')" :rules="ruleNotEmpty"></v-text-field>
							</v-col>
						</v-row>
						<v-row>
							<v-col>
								<v-textarea v-model="extraWork.comment" rows="3" :label="$t('apikey.comment')"></v-textarea>
							</v-col>
						</v-row>
					</v-container>
				</v-form>
			</div>
			<v-card-actions class="d-flex justify-end">
				<v-btn color="primary" @click="saveReport" :disabled="!isValid">
					{{ $t('action_save') }}
				</v-btn>
				<v-btn variant="text" @click="cancel">
					{{ $t('action_cancel') }}
				</v-btn>
			</v-card-actions>
		</v-card>
	</div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, watch, nextTick, onBeforeMount } from 'vue';
import { useI18n } from 'vue-i18n';
import i18n from '@/plugins/i18n';
import { DateTime } from 'luxon';
import { storeToRefs } from 'pinia';

import DateEntryForm from '../../../../../../components/DateAndTime/DateEntryForm/DateEntryForm.vue';
import TimeEntryForm from '../../../../../../components/DateAndTime/TimeEntryForm/TimeEntryForm.vue';
import AddCommentDialog from '../../../../../../components/common/AddCommentDialog.vue';

import { isValid24HourTime, isDurationWithinLimit } from '../../../../../../components/utils/utils.js';
import useDateTime from '../../../../../../components/composables/useDateTime.js';

import { useSetupBridgeStore } from '@/rennovation/planning-view/store/bridge/setupBridge';
import { useReportStore } from '@/rennovation/planning-view/store/useReportStore';
import { useDutyStore } from '@/rennovation/planning-view/store/useDutyStore';
import { Duty, DutyBreak } from '@/rennovation/planning-view/types/api/duties-response';
import { ExtraWorkRequest, ExtraWorkResponse } from '@/rennovation/planning-view/types/api/extra-work-response';
import { ValidationRule } from 'vuetify/framework';

const props = defineProps<{
	selectedDuty: Duty;
	selectedDate: string;
	report: ExtraWorkResponse | null;
	mode: string | null;
}>();

const emit = defineEmits(['cancel', 'submit']);

const { t } = useI18n();
const { formatDateTime } = useDateTime();

const setupBridge = useSetupBridgeStore();
const reportStore = useReportStore();
const dutyStore = useDutyStore();

const { setup } = storeToRefs(setupBridge);

const reportFormRef = ref<InstanceType<typeof VForm> | null>(null);

const startDateInput = ref<InstanceType<typeof DateEntryForm> | null>(null);
const endDateInput = ref<InstanceType<typeof DateEntryForm> | null>(null);

const startTimeInput = ref<InstanceType<typeof TimeEntryForm> | null>(null);
const endTimeInput = ref<InstanceType<typeof TimeEntryForm> | null>(null);

const isValid = ref(false);
const showCommentModal = ref(false);
const pendingReportData = ref<ExtraWorkRequest | null>(null);
const reasons = ref<string[]>([]);
const maxTime = 12; // A single extra work time diff cannot be more that maxTime

const extraWork = reactive({
	dutyName: null as string | null,
	dutyID: null as string | null,
	startDate: null as string | null,
	startTime: null as string | null,
	createdAt: null as string | null,
	endDate: null as string | null,
	endTime: null as string | null,
	reason: null as string | null,
	other: null as string | null,
	comment: '',
	status: t('absence.pending'),
});

const statues = [t('absence.pending'), t('absence.approved'), t('absence.rejected')];
const ruleNotEmpty: ValidationRule[] = [
	(v: string | null | undefined) => {
		return (!!v && v.trim() !== '') || i18n.t('report.form.required');
	},
];

const statusMapToApi = computed<Record<string, string>>(() => ({
	[t('absence.pending')]: 'pending',
	[t('absence.approved')]: 'approved',
	[t('absence.rejected')]: 'rejected',
}));

const statusMapToI18n = computed<Record<string, string>>(() => ({
	pending: t('absence.pending'),
	approved: t('absence.approved'),
	rejected: t('absence.rejected'),
}));

const commentModalTitle = computed(() => t('add_comment_title', { module: t('extra_work_lowercase') }));

const dutyDateISO = computed(() => (props.selectedDate ? props.selectedDate.toISODate() : ''));

const nextDayISO = computed(() => (props.selectedDate ? props.selectedDate.plus({ days: 1 }).toISODate() : ''));

const startDateRules = computed(() => {
	const minDate = dutyDateISO.value;
	const maxDate = nextDayISO.value;

	if (!extraWork?.startDate) return [true];

	const start = DateTime.fromISO(extraWork?.startDate);
	if (!start.isValid) return [t('report.form.invalid_date')];

	if (start < DateTime.fromISO(minDate) || start > DateTime.fromISO(maxDate)) {
		return [t('report.form.invalid_time.start_date_out_of_range')];
	}
	return [true];
});

const endDateRules = computed(() => {
	const minDate = extraWork?.startDate;
	const maxDate = nextDayISO.value;

	if (!extraWork?.endDate) return [true];

	const end = DateTime.fromISO(extraWork?.endDate);
	if (!end.isValid) return [t('report.form.invalid_date')];

	if (end < DateTime.fromISO(minDate!) || end > DateTime.fromISO(maxDate)) {
		return [t('report.form.invalid_time.end_date_out_of_range')];
	}
	return [true];
});

const startTimeRules = computed(() => {
	if (!isValid24HourTime(extraWork?.startTime)) {
		return [i18n.t('time_invalid')];
	}

	const tenantZone = setup.value.planning_timezone;
	const extraStart = parseExtraWorkTime(extraWork?.startDate, extraWork?.startTime, tenantZone);
	const extraEnd = parseExtraWorkTime(extraWork?.endDate, extraWork?.endTime, tenantZone);

	if (extraStart === null || extraEnd === null) {
		return [true];
	}

	if (extraStart >= extraEnd) {
		return [i18n.t('report.form.invalid_time.start')];
	}
	if (isDurationWithinLimit(extraStart, extraEnd, maxTime)) {
		return [i18n.t('report.form.invalid_time.max_duration', { max: maxTime })];
	}

	const midnightUTC = DateTime.fromISO(props.selectedDuty?.dayObject?.midnight, { zone: 'utc' });
	const dutyStart = midnightUTC.plus({ seconds: props.selectedDuty?.start });
	const dutyEnd = midnightUTC.plus({ seconds: props.selectedDuty?.end });

	const mergeBreakIntervals = getClampedAndMergedBreakIntervals(props.selectedDuty.breaks, midnightUTC, dutyStart, dutyEnd);
	const validMergedBreaks = Array.isArray(mergeBreakIntervals) ? mergeBreakIntervals : [];
	const extraStartBeforeDutyStartOrAfterDutyEnd = extraStart < dutyStart || extraStart >= dutyEnd;

	if (extraStartBeforeDutyStartOrAfterDutyEnd) {
		return [true];
	}

	const isExtraStartWithinAnyBreak = validMergedBreaks.some((br) => br && extraStart >= br?.start && extraStart < br?.end);
	return isExtraStartWithinAnyBreak ? [true] : [i18n.t('report.form.invalid_time.overlaps_with_duty')];
});

const endTimeRules = computed(() => {
	if (!isValid24HourTime(extraWork?.endTime)) {
		return [i18n.t('time_invalid')];
	}
	const tenantZone = setup.value.planning_timezone;
	const extraStart = parseExtraWorkTime(extraWork?.startDate, extraWork?.startTime, tenantZone);
	const extraEnd = parseExtraWorkTime(extraWork?.endDate, extraWork?.endTime, tenantZone);

	if (extraStart === null || extraEnd === null) {
		return [true];
	}

	if (extraStart >= extraEnd) {
		return [i18n.t('report.form.invalid_time.end')];
	}
	if (isDurationWithinLimit(extraStart, extraEnd, maxTime)) {
		return [i18n.t('report.form.invalid_time.max_duration', { max: maxTime })];
	}

	const midnightUTC = DateTime.fromISO(props.selectedDuty?.dayObject?.midnight, { zone: 'utc' });
	const dutyStart = midnightUTC.plus({ seconds: props.selectedDuty?.start });
	const dutyEnd = midnightUTC.plus({ seconds: props.selectedDuty?.end });

	const mergeBreakIntervals = getClampedAndMergedBreakIntervals(props.selectedDuty?.breaks, midnightUTC, dutyStart, dutyEnd);
	const validMergedBreaks = Array.isArray(mergeBreakIntervals) ? mergeBreakIntervals : [];

	if (extraStart >= dutyEnd) {
		return [true];
	}

	const dutyIntervals = getDutyIntervals(validMergedBreaks, dutyStart, dutyEnd);

	if (extraStart < dutyStart) {
		const hasOverlap = dutyIntervals.some((interval) => interval && extraStart < interval?.end && extraEnd > interval?.start);
		if (hasOverlap) {
			return [i18n.t('report.form.invalid_time.overlaps_with_duty')];
		}
		return [true];
	}

	if (extraStart >= dutyStart && extraStart < dutyEnd) {
		const hasOverlap = dutyIntervals.some((interval) => extraStart < interval?.end && extraEnd > interval?.start);
		return hasOverlap ? [i18n.t('report.form.invalid_time.overlaps_with_duty')] : [true];
	}

	return [true];
});

const revalidateDateTimeFields = () => {
	startDateInput.value?.validate();
	endDateInput.value?.validate();
	startTimeInput.value?.validate();
	endTimeInput.value?.validate();
};

watch(
	() => extraWork.startDate,
	() => {
		nextTick(revalidateDateTimeFields);
	}
);
watch(
	() => extraWork.endDate,
	() => {
		nextTick(revalidateDateTimeFields);
	}
);
watch(
	() => extraWork.startTime,
	() => {
		nextTick(revalidateDateTimeFields);
	}
);
watch(
	() => extraWork.endTime,
	() => {
		nextTick(revalidateDateTimeFields);
	}
);

const cancel = () => {
	emit('cancel');
};

const getDutyIntervals = (mergedBreaks: DutyBreak[], dutyStart: DateTime, dutyEnd: DateTime) => {
	const workingPeriods = [];
	let currentStart = dutyStart;

	for (const br of mergedBreaks) {
		if (currentStart < br?.start) {
			workingPeriods.push({ start: currentStart, end: br.start });
		}
		currentStart = br?.end > currentStart ? br?.end : currentStart;
	}

	if (currentStart < dutyEnd) {
		workingPeriods.push({ start: currentStart, end: dutyEnd });
	}
	return workingPeriods;
};

const getClampedAndMergedBreakIntervals = (breaks: DutyBreak[], midnightUTC: DateTime, dutyStart: DateTime, dutyEnd: DateTime) => {
	if (!breaks || breaks.length === 0) return [];

	const breaksWithDateTime = breaks.map((b) => ({
		start: midnightUTC.plus({ seconds: b.start }),
		end: midnightUTC.plus({ seconds: b.end }),
	}));

	const clamped = breaksWithDateTime
		.map((b) => {
			const start = dutyStart > b?.start ? dutyStart : b?.start;
			const end = dutyEnd < b?.end ? dutyEnd : b?.end;
			return { start, end };
		})
		.filter((b) => b?.start < b?.end);

	clamped.sort((a, b) => a.start.toMillis() - b.start.toMillis());

	const merged = [];
	let current = clamped[0];

	for (let i = 1; i < clamped.length; i++) {
		const next = clamped[i];
		const breaksAdjacentOrOverlapping = current.end.equals(next?.start) || current.end > next?.start;

		if (breaksAdjacentOrOverlapping) {
			const nextBreakEndsLater = next?.end > current?.end;
			if (nextBreakEndsLater) {
				current = { start: current?.start, end: next?.end };
			}
		} else {
			merged.push(current);
			current = next;
		}
	}
	merged.push(current);
	return merged;
};

const parseExtraWorkTime = (dateISO: string | null, timeStr: string | null, tenantZone: string) => {
	if (!timeStr || !dateISO || timeStr.length < 5 || !isValid24HourTime(timeStr)) return null;

	const [hourStr, minuteStr] = timeStr.split(':');
	const hour = parseInt(hourStr, 10);
	const minute = parseInt(minuteStr, 10);

	const date = DateTime.fromISO(dateISO, { zone: tenantZone });
	if (!date.isValid) return null;

	return date.plus({ hours: hour, minutes: minute }).toUTC();
};

const createDate = (time: string | null, date: string | null) => {
	if (time && date) {
		const hour = time.substr(0, 2);
		const minute = time.substr(3, 5);
		let newDate = new Date(date);
		newDate.setHours(parseInt(hour, 10));
		newDate.setMinutes(parseInt(minute, 10));
		return newDate;
	}
	return null;
};

const populateForm = async () => {
	await setupBridge.loadSetup();
	reasons.value = [...setup.value.extraWork_reasons, t('report.reason.other')];
	if (props.mode === 'edit' && props.report !== null) {
		try {
			const duty = await dutyStore.fetchDutyByID(props.report?.duty);
			const reportStart = DateTime.fromISO(props.report.details.start);
			const reportEnd = DateTime.fromISO(props.report.details.end);

			extraWork.startDate = reportStart.toString();
			extraWork.endDate = reportEnd.toString();
			const startMinute = reportStart.minute.toString().padStart(2, '0');
			const endMinute = reportEnd.minute.toString().padStart(2, '0');
			const startHour = reportStart.hour.toString().padStart(2, '0');
			const endHour = reportEnd.hour.toString().padStart(2, '0');
			extraWork.startTime = startHour + ':' + startMinute;
			extraWork.endTime = endHour + ':' + endMinute;

			extraWork.dutyID = props.report.duty;
			extraWork.dutyName = duty.name;
			extraWork.createdAt = props.report.createdAt;
			extraWork.reason = props.report.details.reason;
			extraWork.comment = props.report.comment;
			extraWork.status = statusMapToI18n.value[props.report.status] ?? t('absence.rejected');
			if (!reasons.value.includes(extraWork.reason)) {
				extraWork.reason = t('report.reason.other');
				extraWork.other = props.report.details.reason;
			}
		} catch (error) {
			console.log(error);
		}
	} else {
		const baseDateISO = props.selectedDate.toISODate();
		const dutyDay = DateTime.fromISO(baseDateISO);

		const dutyEndDT = dutyDay.plus({ seconds: props.selectedDuty.end });
		const extraEndDT = dutyEndDT.plus({ hours: 1 });

		extraWork.startDate = dutyEndDT.toISODate();
		extraWork.endDate = extraEndDT.toISODate();

		extraWork.startTime = dutyEndDT.toFormat('HH:mm');
		extraWork.endTime = extraEndDT.toFormat('HH:mm');

		extraWork.dutyID = props.selectedDuty.id;
		extraWork.dutyName = props.selectedDuty.name;
		extraWork.createdAt = baseDateISO;
	}
};

const saveReport = async () => {
	const { valid } = await reportFormRef.value.validate();
	if (valid) {
		const { comment, dutyID, day, startTime, startDate, endTime, endDate, status } = extraWork;
		const start = createDate(startTime, startDate);
		const end = createDate(endTime, endDate);

		const report = {
			type: 'extraWork',
			user: props.report?.user ? props.report.user : props.selectedDuty.user,
			day: day,
			duty: dutyID,
			comment: comment?.trim() || '',
			status: statusMapToApi.value[status] ?? 'rejected',
			details: {
				start: start,
				end: end,
				reason: extraWork.reason === t('report.reason.other') ? extraWork.other : extraWork.reason,
			},
		};

		pendingReportData.value = report;
		toggleShowCommentModal(true);
	}
};

const handleCommentSubmission = async (comment: string) => {
	if (!pendingReportData.value) return;

	const reportWithChangeComment = {
		...pendingReportData.value,
		changeComment: comment?.trim() || '',
	};

	if (props.mode === 'edit') {
		await reportStore.editReport(props.report._id ?? props.report.id, reportWithChangeComment);
	} else {
		await reportStore.createNewReport(reportWithChangeComment);
	}
	toggleShowCommentModal(false);
	pendingReportData.value = null;
	emit('submit');
};

const toggleShowCommentModal = (show = false) => {
	showCommentModal.value = show;
};

onBeforeMount(async () => {
	await populateForm();
});
</script>

<style scoped>
.report-form-body {
	height: 77vh;
	overflow-y: auto;
	overflow-x: hidden;
}
</style>
